import TitlePage from '@/components/TitlePage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useChangePassword } from '@/hooks/useEmployee';
import LoadingCircle from '@/components/LoadingCircle';
import { Eye, EyeOff } from 'lucide-react';
import { useState, useEffect } from 'react';
import { ChangePasswordFormValues } from '@/schemas/change-pass';
import { changePasswordSchema } from '@/schemas/change-pass';

const ChangePassword = () => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { mutateAsync: changePassword, isPending } = useChangePassword();

  const form = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });

  const newPassword = form.watch('newPassword');
  const confirmPassword = form.watch('confirmPassword');

  useEffect(() => {
    if (newPassword && confirmPassword) {
      form.trigger('confirmPassword');
    }
  }, [newPassword, form, confirmPassword]);

  const onSubmit = async (values: ChangePasswordFormValues) => {
    if (values.newPassword !== values.confirmPassword) {
      form.setError('confirmPassword', {
        message: 'Mật khẩu mới và xác nhận mật khẩu không khớp',
      });
      return;
    }

    await changePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    });
  };

  return (
    <div className="luxury-theme min-h-screen">
      <div className="flex items-center justify-between bg-white p-4 border-b border-luxury-200 h-[69px]">
        <div className="flex items-center gap-3">
          <div className="text-2xl text-luxury-900">
            <TitlePage />
          </div>
        </div>
      </div>
      <div className="p-4 relative h-[calc(100vh-69px)] flex items-center justify-center">
        <div className="w-full max-w-md bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-medium text-luxury-900 mb-6 pb-2 border-b border-luxury-100">
            Đổi mật khẩu
          </h2>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <FormField
                control={form.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <Label className="luxury-label">Mật khẩu hiện tại</Label>
                    <div className="relative">
                      <FormControl>
                        <Input
                          {...field}
                          type={showCurrentPassword ? 'text' : 'password'}
                          className="luxury-input pr-10"
                        />
                      </FormControl>
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() =>
                          setShowCurrentPassword(!showCurrentPassword)
                        }
                      >
                        {showCurrentPassword ? (
                          <EyeOff className="h-4 w-4 text-luxury-500" />
                        ) : (
                          <Eye className="h-4 w-4 text-luxury-500" />
                        )}
                      </button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <Label className="luxury-label">Mật khẩu mới</Label>
                    <div className="relative">
                      <FormControl>
                        <Input
                          {...field}
                          type={showNewPassword ? 'text' : 'password'}
                          className="luxury-input pr-10"
                        />
                      </FormControl>
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-4 w-4 text-luxury-500" />
                        ) : (
                          <Eye className="h-4 w-4 text-luxury-500" />
                        )}
                      </button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <Label className="luxury-label">
                      Xác nhận mật khẩu mới
                    </Label>
                    <div className="relative">
                      <FormControl>
                        <Input
                          {...field}
                          type={showConfirmPassword ? 'text' : 'password'}
                          className="luxury-input pr-10"
                        />
                      </FormControl>
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="h-4 w-4 text-luxury-500" />
                        ) : (
                          <Eye className="h-4 w-4 text-luxury-500" />
                        )}
                      </button>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full luxury-button-primary"
                disabled={isPending}
              >
                {isPending ? <LoadingCircle /> : 'Đổi mật khẩu'}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
